import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  HostListener,
  AfterViewInit,
  ViewContainerRef,
  Renderer2,
  OnDestroy,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';

@Component({
  standalone: false,
  selector: 'nimbus-tooltip',
  templateUrl: './nimbus-tooltip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusTooltipComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() public text = '';
  @Input() public ariaLabel: string;
  @Input() public position: 'top' | 'above' | 'left' | 'right' | 'bottom' | 'below' = 'above';
  @Input() public showArrow: boolean = true;
  @Input() public theme: ChartTheme = ChartTheme.Default;
  @Input() public showDelay: number = 400;
  @Input() public sticky: boolean = false;
  @Input() public panelClass: string = '';

  @ViewChild(MatTooltip) public matTooltip: MatTooltip;

  private parentElement: HTMLElement | null = null;
  private originalParentAriaLabel: string | null = null;
  private appliedParentAriaLabel: string | null = null;
  private removeMouseOverEventListener: () => void;
  private removeMouseLeaveEventListener: () => void;

  public matTooltipPosition: string;
  public matTooltipClass: string;

  public constructor(
    private readonly viewRef: ViewContainerRef,
    private readonly renderer: Renderer2,
  ) {}

  @HostListener('mouseover', ['$event'])
  public onMouseOver(event: MouseEvent): void {
    this.open(event);
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    if (!this.sticky) {
      this.matTooltip?.hide(0);
    }
  }

  public ngOnInit(): void {
    this.matTooltipPosition = this.getMatTooltipPosition();
    this.matTooltipClass = ['nimbus-mat-tooltip', this.getMatTooltipArrowClass(), this.getMatTooltipThemeClass(), this.panelClass]
      .filter(Boolean)
      .join(' ');
  }

  public ngAfterViewInit(): void {
    const parent = this.viewRef.element.nativeElement?.parentElement;
    this.parentElement = parent ?? null;

    if (parent) {
      this.originalParentAriaLabel = parent.getAttribute('aria-label');
      this.applyParentAriaLabel();

      this.removeMouseOverEventListener = this.renderer.listen(parent, 'mouseover', (event: MouseEvent) => {
        this.open(event);
      });

      this.removeMouseLeaveEventListener = this.renderer.listen(parent, 'mouseleave', () => {
        this.close();
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['text'] || changes['ariaLabel']) && this.parentElement) {
      this.applyParentAriaLabel();
    }
  }

  public ngOnDestroy(): void {
    if (this.removeMouseOverEventListener) {
      this.removeMouseOverEventListener();
    }

    if (this.removeMouseLeaveEventListener) {
      this.removeMouseLeaveEventListener();
    }

    // Restore the parent's own label so it does not keep announcing the tooltip text after the tooltip is removed.
    // Skip it when the label is no longer the one set here, e.g. the parent's [attr.aria-label] binding updated it meanwhile.
    if (this.parentElement && this.parentElement.getAttribute('aria-label') === this.appliedParentAriaLabel) {
      if (this.originalParentAriaLabel === null) {
        this.renderer.removeAttribute(this.parentElement, 'aria-label');
      } else {
        this.renderer.setAttribute(this.parentElement, 'aria-label', this.originalParentAriaLabel);
      }
    }
  }

  public open(event?: MouseEvent): void {
    const origin = event ? { x: event.clientX, y: event.clientY } : undefined;
    this.matTooltip.show(this.showDelay, origin);
  }

  public close(): void {
    this.matTooltip.hide();
  }

  private applyParentAriaLabel(): void {
    this.appliedParentAriaLabel = this.ariaLabel || this.text;
    this.renderer.setAttribute(this.parentElement, 'aria-label', this.appliedParentAriaLabel);
  }

  private getMatTooltipPosition(): string {
    switch (this.position) {
      case 'top':
        return 'above';
      case 'bottom':
        return 'below';
      default:
        return this.position;
    }
  }

  private getMatTooltipArrowClass(): string {
    return this.showArrow ? 'nimbus-mat-tooltip-arrow' : '';
  }

  private getMatTooltipThemeClass(): string {
    switch (this.theme) {
      case ChartTheme.Dark:
        return 'theme-dark';
      case ChartTheme.HighContrast:
        return 'theme-contrast';
      default:
        return 'theme-default';
    }
  }
}
